import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import Button from "@/components/Button";
import Input from "@/components/Input";
import Select from "@/components/Select";
import Icons from "@/assets/icons";
import { useFormik } from "formik";
import { useTranslation } from "react-i18next";
import { ApiService } from "@/services/ApiService";
import { messageService } from "@/services/MessageService";
import { validateFormikFields } from "@/lib/utils";
import RegisterProductSchema from "./Schema/RegisterProductSchema";
import { useEffect, useState } from "react";

interface ProductDialogProps {
  open: boolean;
  onClose: () => void;
  productToEdit?: any | null;
  onSuccess: () => void;
}

const ProductDialog = ({ open, onClose, productToEdit, onSuccess }: ProductDialogProps) => {
  const { t } = useTranslation();
  const { post, patch } = ApiService;
  const [schema] = useState(RegisterProductSchema(t));

  const initialValues = {
    nomeProduto: "",
    status: "ATIVO",
  };

  const formik = useFormik({
    initialValues,
    validate: (values) => validateFormikFields(values, schema),
    onSubmit: async (values) => {
      try {
        const request = productToEdit?.id
          ? patch(`api/v1/produto/atualizar/${productToEdit.id}`, values)
          : post("api/v1/produto/registrar", values);

        const { status } = await request;

        if (status === 200) {
          messageService.success(
            productToEdit ? t("productsForm.messages.update.title") : t("productsForm.messages.save.title"),
            productToEdit ? t("productsForm.messages.update.description") : t("productsForm.messages.save.description")
          );
          onSuccess();
          handleClose();
        }
      } catch (error: any) {
        if (error.response?.status === 409) {
             messageService.error(
                 t("productsForm.messages.error.title", "Erro"),
                 error.response.data?.message || error.message
             );
        } else {
             console.error(error);
        }
      }
    },
  });

  useEffect(() => {
    if (open) {
        if (productToEdit) {
            formik.setValues({
                nomeProduto: productToEdit.nomeProduto || "",
                status: productToEdit.status || "ATIVO",
            });
        } else {
            formik.resetForm();
        }
    }
  }, [open, productToEdit]);

  const handleClose = () => {
    formik.resetForm();
    onClose();
  };

  const statusOptions = [
    { title: t("table.user.body.active.true", "Ativo"), value: "ATIVO" },
    { title: t("table.user.body.active.false", "Inativo"), value: "INATIVO" },
  ];

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px] bg-white p-6 rounded-lg">
        <DialogHeader>
          <DialogTitle className="text-xl font-semibold text-gray-800 mb-4">
            {productToEdit ? t("productsForm.titleEdit") : t("productsForm.title")}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={formik.handleSubmit} className="flex flex-col gap-6">
          <Input
            id="nomeProduto"
            label={t("productsForm.inputs.nomeProduto.label")}
            placeholder="Digite o nome do produto"
            error={
              formik.errors.nomeProduto && formik.touched.nomeProduto
                ? formik.errors.nomeProduto
                : ""
            }
            {...formik.getFieldProps("nomeProduto")}
          />

          <Select
            id="status"
            label={t("table.user.head.active", "Status")}
            placeholder="Selecione o status"
            options={statusOptions}
            error={
              formik.errors.status && formik.touched.status
                ? (formik.errors.status as string)
                : ""
            }
            {...formik.getFieldProps("status")}
          />

          <div className="flex justify-end gap-3 mt-4">
            <Button
              label={t("productsForm.buttons.cancel")}
              color="neutro"
              onClick={handleClose}
              type="button"
              icon={<Icons.Close pathStyle="fill-inherit" />}
            /> 
            
            <Button
              label={t("productsForm.buttons.save")}
              color="primario"
              type="submit"
              disabled={formik.isSubmitting}
              icon={<Icons.Save pathStyle="fill-inherit" />}
            />

          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default ProductDialog;